// Microsoft Clarity helper.
//
// Clarity is loaded ONLY after the visitor explicitly accepts analytical cookies
// (see cookieConsentBanner.tsx). Nothing from Microsoft is requested before that,
// which keeps the app GDPR-compliant without a Consent Mode setup.

const CONSENT_KEY = "hf-analytics-consent";

// Bump when the SET of tracking tools covered by consent changes, so previously
// stored consent no longer applies and the banner re-prompts.
//   v1 = Microsoft Clarity only
const CONSENT_VERSION = 1;

// Consent expires after this many days (both granted and denied), after which
// the banner reappears for a fresh choice. ~6 months.
const CONSENT_MAX_AGE_DAYS = 182;

export type Consent = "granted" | "denied";

interface StoredConsent {
	v: number;
	value: Consent;
	ts: number;
}

/**
 * Read the stored consent choice, or null if the visitor must (re)choose.
 * Returns null when there is no stored choice, when it was given under an older
 * CONSENT_VERSION, when it is older than CONSENT_MAX_AGE_DAYS, or when the stored
 * data is malformed — all of which re-prompt.
 */
export const getConsent = (): Consent | null => {
	try {
		const raw = localStorage.getItem(CONSENT_KEY);
		if (!raw) { return null; }

		const parsed = JSON.parse(raw) as StoredConsent;
		if (parsed?.v !== CONSENT_VERSION) { return null; }
		if (parsed.value !== "granted" && parsed.value !== "denied") { return null; }
		if (Date.now() - parsed.ts >= CONSENT_MAX_AGE_DAYS * 864e5) { return null; }

		return parsed.value;
	} catch {
		// malformed JSON or localStorage unavailable — treat as no choice
		return null;
	}
};

/** Persist the visitor's consent choice, stamped with the current version and time. */
export const setConsent = (value: Consent): void => {
	try {
		const stored: StoredConsent = { v: CONSENT_VERSION, value, ts: Date.now() };
		localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
	} catch {
		/* localStorage unavailable — ignore */
	}
};

/** Clear the stored consent choice so the banner reappears next load. */
export const clearConsent = (): void => {
	try {
		localStorage.removeItem(CONSENT_KEY);
	} catch {
		/* localStorage unavailable — ignore */
	}
};

/**
 * Inject the Microsoft Clarity tag (heatmaps + session recordings). Idempotent,
 * and a no-op when no project ID is configured (e.g. localhost).
 */
export const loadClarity = (projectId: string): void => {
	if (!projectId) { return; }
	if (window.clarity) { return; } // already loaded

	// Same behaviour as Microsoft's official snippet: a queueing stub collects calls
	// until the real tag loads and drains `clarity.q`. Appended to <head> rather than
	// inserted before the first <script> — the official snippet throws when a document
	// happens to have no script tag yet.
	const queue: unknown[][] = [];
	const clarity = ((...args: unknown[]) => { queue.push(args); }) as NonNullable<Window["clarity"]> & { q: unknown[][] };
	clarity.q = queue;
	window.clarity = clarity;

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.clarity.ms/tag/${projectId}`;
	document.head.appendChild(script);

	// Clarity's Consent Mode is ON by default for EEA/UK/CH visitors, and since
	// 31 Oct 2025 a consent signal is enforced there. Without it Clarity stays in
	// limited "no-consent mode" (no cookies, no cross-session tracking). loadClarity
	// only runs after the visitor accepts analytical cookies, so signal granted
	// analytics (we don't run ads, hence ad_Storage denied). Queued on the stub
	// above; applied once the tag script finishes loading.
	clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "granted" });
};

/**
 * Tell Clarity consent was withdrawn: erases its cookies and stops tracking.
 * No-op if Clarity was never loaded on this page.
 */
export const revokeClarity = (): void => {
	if (!window.clarity) { return; }
	window.clarity("consent", false);
};
